import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Moon, Sun, Shield, Rocket, CheckCircle, AlertCircle, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const DeployArea = ({ user, onBack, onLogout }) => {
  const [darkMode, setDarkMode] = useState(false);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-elegant-md">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Deploy/Updates</h1>
              <p className="text-xs text-muted-foreground">
                Conectat ca: {user?.full_name}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={toggleDarkMode}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={onLogout}
              className="rounded-xl border-border hover:bg-destructive hover:text-destructive-foreground transition-smooth"
              title="Deconectare"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          {/* Welcome Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Deploy & Updates
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Gestionare deploy-uri, actualizări sistem și configurări avansate.
            </p>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-card border-border rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status Sistem</p>
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">Operational</p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Versiune Curentă</p>
                  <p className="text-xl font-bold text-foreground">v1.0.0</p>
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Actualizări</p>
                  <p className="text-xl font-bold text-foreground">0 Pending</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Deploy Actions */}
          <Card className="bg-card border-border rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-foreground mb-6">Acțiuni Deploy</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                className="h-auto py-6 rounded-xl flex-col gap-2"
                variant="outline"
                onClick={() => toast.info('Funcționalitate în dezvoltare')}
              >
                <Rocket className="w-6 h-6" />
                <div>
                  <div className="font-semibold">Deploy Frontend</div>
                  <div className="text-xs text-muted-foreground">Actualizează interfața</div>
                </div>
              </Button>

              <Button
                className="h-auto py-6 rounded-xl flex-col gap-2"
                variant="outline"
                onClick={() => toast.info('Funcționalitate în dezvoltare')}
              >
                <Rocket className="w-6 h-6" />
                <div>
                  <div className="font-semibold">Deploy Backend</div>
                  <div className="text-xs text-muted-foreground">Actualizează server-ul</div>
                </div>
              </Button>

              <Button
                className="h-auto py-6 rounded-xl flex-col gap-2"
                variant="outline"
                onClick={() => toast.info('Funcționalitate în dezvoltare')}
              >
                <Shield className="w-6 h-6" />
                <div>
                  <div className="font-semibold">Backup Database</div>
                  <div className="text-xs text-muted-foreground">Salvare date</div>
                </div>
              </Button>

              <Button
                className="h-auto py-6 rounded-xl flex-col gap-2"
                variant="outline"
                onClick={() => toast.info('Funcționalitate în dezvoltare')}
              >
                <CheckCircle className="w-6 h-6" />
                <div>
                  <div className="font-semibold">Health Check</div>
                  <div className="text-xs text-muted-foreground">Verificare sistem</div>
                </div>
              </Button>
            </div>
          </Card>

          {/* Info Note */}
          <Card className="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 rounded-2xl p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Atenție!</h4>
                <p className="text-sm text-muted-foreground">
                  Acțiunile de deploy pot afecta disponibilitatea aplicației. Asigură-te că ai făcut backup recent înainte de a efectua actualizări.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DeployArea;
